import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useUnConfigBootStoreContext } from 'views/BootGuide/contexts/UnConfigBootStoreContext';
import { UnConfigStore } from './share';
import { useMount, useUnmount } from 'ahooks';
import {
  getBootProcessing,
  getDefaultValueRule,
  postBootProcessing,
} from 'views/BootGuide/service';
import { StoreScopesModal, StoreScopesProps, useStoreMap } from 'components/StoreScopes';
import useModal from 'hooks/useModal';
import { Formik, FormikConfig } from 'formik';
import { Modal, ModalProps } from 'antd';
import { ConfigPanel } from 'views/BootGuide/components/ConfigPanel';
import { useStepName } from 'views/BootGuide/hooks/useStepName';
import { useTranslate } from '@tolgee/react';
import { useNextStep } from 'views/BootGuide/hooks/useNextStep';
import styled from '@emotion/styled';
import avatarImg from '../../assets/avatar.png';
import { initProgressBodyAiModel } from 'views/BootGuide/hooks/useInitial';

function useViewState<P>() {
  const stepName = useStepName();

  const unConfigStores = useUnConfigBootStoreContext();
  const stores = useMemo(
    () =>
      unConfigStores.map(
        ({ storeId, externalStoreId, ...others }) =>
          ({
            id: Number(storeId),
            externalId: externalStoreId,
            ...others,
          } as UnConfigStore),
      ),
    [unConfigStores],
  );

  const [defaultValueRuleLoading, setDefaultValueRuleLoading] = useState(true);
  const [defaultValueRule, setDefaultValueRule] = useState<BootGuide.AiModel.DefaultValueRule>();
  useMount(() => {
    getDefaultValueRule()
      .then(setDefaultValueRule)
      .catch(() => {})
      .finally(() => {
        setDefaultValueRuleLoading(false);
      });
  });

  const [initializing, setInitializing] = useState(true);
  const [editingValues, setEditingValues] = useState<P>();
  const editingValuesRef = useRef<P>();
  const storeMap = useStoreMap({
    list: stores,
  });

  useMount(() => {
    getBootProcessing(true)
      .then(() => {
        setInitializing(false);
      })
      .catch(() => {
        setInitializing(false);
      });
  });

  const shouldCacheRef = useRef(stepName === 'AI_MODEL_STORE' ? true : false);

  useUnmount(() => {
    setTimeout(() => {
      if (stepName === 'AI_MODEL_STORE' && !shouldCacheRef.current) {
        return;
      }

      getBootProcessing(true)
        .then((cache) => {
          const _basicCache = cache.find((_) => _.configType === 'AI_ASSISTANT_BOT');

          if (stepName === 'AI_MODEL_LOGISTICS' && !shouldCacheRef.current && !_basicCache) {
            return;
          }

          const basicCache = _basicCache || initProgressBodyAiModel(unConfigStores);

          if (!basicCache) {
            return;
          }

          const content = (() => {
            try {
              return JSON.parse(basicCache.content) || {};
            } catch (_) {
              return {};
            }
          })();

          return postBootProcessing({
            ...basicCache,
            configType: 'AI_ASSISTANT_BOT',
            stepName,
            status: 'IN_PROGRESS',
            content: JSON.stringify({
              ...content,
              aiModelState: {
                ...content.aiModelState,
                [stepName]: {
                  editingValues: editingValuesRef.current || editingValues,
                },
              },
            }),
          });
        })
        .catch(() => {});
    }, 1000);
  });

  const storeScopesModalState = useModal<StoreScopesProps & { key: any }>();

  const storeScopesModal = storeScopesModalState.meta && (
    <StoreScopesModal
      allowEmpty
      showFooterTips={false}
      visible={storeScopesModalState.visible}
      onCancel={() => storeScopesModalState.hide()}
      {...storeScopesModalState.meta}
    />
  );

  const editModalState = useModal<{
    key?: any;
    formikConfig: FormikConfig<any>;
    modalProps: ModalProps;
  }>();

  const { meta: editMeta } = editModalState;

  const editModal = editMeta && (
    <Formik
      key={editMeta.key}
      enableReinitialize
      {...editMeta.formikConfig}
      onSubmit={(...props) => {
        editMeta.formikConfig.onSubmit(...props);
        editModalState.hide();
      }}
    >
      {({ handleSubmit }) => {
        return (
          <Modal
            visible={editModalState.visible}
            width={602}
            onCancel={() => editModalState.hide()}
            onOk={() => handleSubmit()}
            {...editMeta.modalProps}
          >
            <EditModalContent>{(editMeta.modalProps as any).children}</EditModalContent>
          </Modal>
        );
      }}
    </Formik>
  );

  const { t } = useTranslate();

  const _nextStep = useNextStep();

  const nextStep = useCallback(() => {
    _nextStep();
    if (stepName === 'AI_MODEL_LOGISTICS') {
      shouldCacheRef.current = true;
    }
    if (stepName === 'AI_MODEL_STORE') {
      shouldCacheRef.current = false;
    }
  }, [_nextStep, stepName]);

  const withPanel = (...nodes: ReactNode[]) => (
    <ConfigPanel autoScroll={false} rightExtraNode={false}>
      <Container>
        <img src={avatarImg} alt="avatar" />
        <div>{...nodes}</div>
      </Container>
    </ConfigPanel>
  );

  return {
    defaultValueRule,
    defaultValueRuleLoading,
    initializing,
    editingValues,
    setEditingValues,
    editingValuesRef,
    stores,
    storeMap,
    storeScopesModal,
    showStoreScopesModal: storeScopesModalState.show,
    editModal,
    showEditModal: editModalState.show,
    t,
    stepName,
    nextStep,
    withPanel,
  };
}

export type ViewState = ReturnType<typeof useViewState>;

const Container = styled.div`
  display: flex;
  padding: 24px;
  > img {
    position: sticky;
    top: 0;
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    margin-right: 12px;
  }
  > div {
    flex: 1 0 0;
    padding: 24px 24px 4px;
    border-radius: 4px;
    background: var(--fett-erp-card-bg);
  }
`;

const EditModalContent = styled.div`
  max-height: calc(100vh - 210px);
  margin: -24px;
  padding: 24px;
  overflow: auto;
`;

export default useViewState;
